<template>
    <div class="page-container" :style="pageStyle">
        <DefaultNav v-bind="navigationProps" />
        <CategoryPage
            v-if="isStoryBookMode"
            v-bind="getPageProps('CategoryPage')"
            :host="$globalConstants.host"
            :host-id="$globalConstants.host.id"
        />

        <keep-alive>
            <router-view
                :host="$globalConstants.host"
                :host-id="$globalConstants.host.id"
            ></router-view>
        </keep-alive>

        <DefaultFooter
            v-if="
                !quizState.loadQuizFirst &&
                !quizState.showQuestionnaire &&
                showFooter
            "
            v-bind="footerProps"
        />

        <PopupDeal
            v-if="
                $globalConstants.host.popup_deal &&
                overlayState.showPopupDeal &&
                !quizState.showQuestionnaire
            "
            v-bind="$globalConstants.components.PopupDeal"
        />

        <NewDatingExitOverlay
            v-if="showExitOverlay"
            :closePopupOverlay="closePopupOverlay"
            :host="$globalConstants.host"
            :popup-open.sync="popupOpen"
        />

        <BackToRanking v-if="showBackToRanking" />

        <BottomBanner
            v-if="
                $globalConstants.host['use_bottom_banner'] &&
                !quizState.showQuestionnaire &&
                categoryState.rankingList
            "
            v-bind="$globalConstants.components.BottomBanner"
            :partner="categoryState.rankingList[0]"
        />

        <SinglePartnerPopupDealAlpha
            v-if="showSinglePartnerPopupAlpha"
            v-bind="$globalConstants.components.SinglePartnerPopupDeal"
        />

        <SinglePartnerPopupDealBeta
            v-if="showSinglePartnerPopupBeta"
            v-bind="$globalConstants.components.SinglePartnerPopupDealBeta"
        />

        <ReturnPopup
            v-if="overlayState.showReturnPopup"
            v-bind="$globalConstants.components.ReturnPopup"
        />
        <portal-target slim name="rating-disclosure" />
    </div>
</template>

<script>
import { quizStore, categoryStore, overlayStore } from '@/store';
import {
    analyticsMixin,
    helper,
    mediaQueriesMixin,
    overlayMixin,
    routeMixin,
} from '@/mixins';
import CategoryPage from '@/application-pages/Category/CategoryPage.vue';
import DefaultNav from '@organisms/Navigation/DefaultNav/DefaultNav';
import DefaultFooter from '@organisms/Footer/DefaultFooter';

export default {
    name: 'DynamicTemplate',
    components: {
        DefaultNav,
        DefaultFooter,
        CategoryPage,
        SinglePartnerPopupDealAlpha: () =>
            import(
                /* webpackChunkName: "SinglePartnerPopupDealAlpha" */ '@organisms/SinglePartnerPopupDeal/SinglePartnerPopupDealAlpha/SinglePartnerPopupDealAlpha'
            ),
        SinglePartnerPopupDealBeta: () =>
            import(
                /* webpackChunkName: "SinglePartnerPopupDealBeta" */ '@organisms/SinglePartnerPopupDeal/SinglePartnerPopupDealBeta/SinglePartnerPopupDealBeta'
            ),
        ReturnPopup: () =>
            import(
                /* webpackChunkName: "ReturnPopup" */ '@organisms/ReturnPopup/ReturnPopup'
            ),
        PopupDeal: () =>
            import(
                /* webpackChunkName: "PopupDeal" */ '@organisms/PopupDeal/PopupDeal'
            ),
        BackToRanking: () =>
            import(
                /* webpackChunkName: "BackToRanking" */ '@/design-system/back-to-ranking/BackToRanking'
            ),
        NewDatingExitOverlay: () =>
            import(
                /* webpackChunkName: "NewDatingExitOverlay" */ '@/design-system/exit_overlay/NewDatingExitOverlay'
            ),
        BottomBanner: () =>
            import(
                /* webpackChunkName: "BottomBanner" */ '@molecules/BottomBanner/BottomBanner'
            ),
    },
    mixins: [
        analyticsMixin,
        overlayMixin,
        routeMixin,
        mediaQueriesMixin,
        helper,
    ],
    methods: {
        getPageProps(page) {
            const pageProps = this.$globalConstants.components[page];
            if (!pageProps) return {};
            return pageProps;
        },
        getPageMenuProps() {
            const page = this.getPageProps('CategoryPage');
            if (page['navigationType'] === 'DefaultNav') {
                return this.$globalConstants.components['DefaultNav'] || [];
            }
            return this.$globalConstants.components['GlobalNav'] || [];
        },
        getPageFooterProps() {
            const page = this.getPageProps('CategoryPage');
            if (page['footerType'] === 'DefaultFooter') {
                return this.$globalConstants.components['DefaultFooter'] || [];
            }
            return [];
        },
    },
    data() {
        return {
            quizState: quizStore.state,
            categoryState: categoryStore.state,
            overlayState: overlayStore.state,
            showSinglePopupBeta: false,
            showFooter: false,
            navigationProps: this.getPageMenuProps(),
            footerProps: this.getPageFooterProps(),
        };
    },
    mounted() {
        if (
            this.$globalConstants.components.SinglePartnerPopupDealBeta
                ?.isDelayed
        ) {
            setTimeout(() => {
                this.showSinglePopupBeta = true;
            }, 2000);
        } else {
            this.showSinglePopupBeta = true;
        }

        setTimeout(() => {
            this.showFooter = true;
        }, 2000);
    },
    computed: {
        pageStyle() {
            return {
                background: this.$globalConstants.components.Settings
                    ? this.$globalConstants.components.Settings['background']
                    : '',
            };
        },
        showBackToRanking() {
            return (
                !this.isCategoryRoute &&
                this.$route.name &&
                this.$globalConstants.host['back_to_ranking']
            );
        },
        showSinglePartnerPopup() {
            const isQuestionnaireEnabled = Boolean(
                this.quizState?.showQuestionnaire
            );
            if (isQuestionnaireEnabled) {
                return false;
            }
            const isPopupClosed = !this.overlayState?.showPopupDeal;
            if (isPopupClosed) {
                return false;
            }
            const isEnabledAtCategoryLevel = Boolean(
                this.categoryState?.category?.use_popup
            );
            if (!isEnabledAtCategoryLevel) {
                return false;
            }
            return true;
        },
        showSinglePartnerPopupAlpha() {
            if (!this.showSinglePartnerPopup) {
                return false;
            }
            const isEnabledAtHostLevel = Boolean(
                this.$globalConstants.host['1_partner_pop_up']
            );
            return !!isEnabledAtHostLevel;
        },
        showSinglePartnerPopupBeta() {
            if (!this.showSinglePartnerPopup) {
                return false;
            }
            const isEnabledAtHostLevel = Boolean(
                this.isMobile()
                    ? this.$globalConstants.host['1_partner_pop_up_mobile']
                    : this.$globalConstants.host['1_partner_pop_up_desktop']
            );
            return !!(isEnabledAtHostLevel && this.showSinglePopupBeta);
        },
    },
};
</script>

<style lang="scss" scoped>
.page-container {
    position: relative;
    align-items: center;
    display: flex;
    flex-direction: column;
}
</style>
